import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import LockRounded from "@mui/icons-material/LockRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import { HIDDEN_RADIO_SX, PlayFormatChips, SkillLevelPicker } from "../../../common/components/PlayStyleFields";
import { MaterialAreaPicker } from "../../../common/components/MaterialAreaPicker";
import { ProfilePhotoUploadField } from "../../../common/components/ProfilePhotoUploadField";
import {
  profileSchema,
  GENDER_OPTIONS,
  type ProfileFormValues,
  type ProfileCreateBody,
} from "../schemas";

interface ProfileStepProps {
  onSubmit: (body: ProfileCreateBody) => void;
  submitting?: boolean;
  consentAccepted?: boolean;
  consent?: ReactNode;
  errorSlot?: ReactNode;
}

const STEPS = [
  { title: "About you", note: "Next: how you play" },
  { title: "How you play", note: "You can change this later" },
  { title: "Photo and agreement", note: "Then you're in" },
] as const;

function SectionLabel({ id, children }: { id: string; children: ReactNode }) {
  return (
    <Typography id={id} component="h3" sx={{ fontSize: "1rem", fontWeight: 700 }}>
      {children}
    </Typography>
  );
}

/**
 * The approved three-step sign-up: About you (name, date of birth, gender,
 * which lock once saved), How you play (events, level, home area), then
 * photo, bio and the prelaunch agreement. All eligibility decisions remain
 * server-owned.
 */
export function ProfileStep({
  onSubmit,
  submitting = false,
  consentAccepted = true,
  consent,
  errorSlot,
}: ProfileStepProps) {
  const [step, setStep] = useState(0);
  const [areaDraft, setAreaDraft] = useState("");
  const titleRef = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);
  // Move focus to the new step's title so keyboard and screen-reader users land on it.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    titleRef.current?.focus();
  }, [step]);
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    trigger,
    getValues,
    formState: { errors, isValid },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    mode: "onChange",
    defaultValues: { display_name: "", date_of_birth: "", play_formats: [] },
  });
  const avatarValue = watch("avatar");

  const submit = handleSubmit((values) => {
    const body: ProfileCreateBody = {
      display_name: values.display_name.trim(),
      date_of_birth: values.date_of_birth,
      gender: values.gender,
    };
    if (values.play_formats?.length) body.play_formats = values.play_formats;
    if (values.skill_level) body.skill_level = values.skill_level;
    if (values.home_area) {
      body.home_area_label = values.home_area.label;
      body.home_latitude = values.home_area.lat;
      body.home_longitude = values.home_area.lng;
    }
    if (values.avatar) body.avatar = values.avatar;
    if (values.bio && values.bio.trim()) body.bio = values.bio.trim();
    onSubmit(body);
  });

  const goNext = async () => {
    if (step === 0) {
      const ok = await trigger(["display_name", "date_of_birth", "gender"]);
      if (!ok) return;
    }
    // From this render's step, so overlapping calls land on the same next step.
    setStep(Math.min(step + 1, STEPS.length - 1));
  };

  const current = STEPS[step];
  const lastStep = step === STEPS.length - 1;

  return (
    <Box
      component="form"
      // Enter before the last step moves on rather than creating the profile.
      onSubmit={(event) => {
        if (lastStep) return void submit(event);
        event.preventDefault();
        void goNext();
      }}
      noValidate
      sx={{ pb: 12 }}
    >
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          {step > 0 ? (
            <IconButton aria-label="Back" edge="start" onClick={() => setStep(step - 1)} disabled={submitting}>
              <ArrowBackRounded />
            </IconButton>
          ) : null}
          <Box>
            <Typography
              ref={titleRef}
              component="h2"
              tabIndex={-1}
              sx={{ fontSize: "1.125rem", fontWeight: 700, outline: "none" }}
            >
              {current.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" aria-live="polite" data-testid="step-caption">
              Step {step + 1} of {STEPS.length}
            </Typography>
          </Box>
        </Stack>

        {step === 0 ? (
          <Stack spacing={2.5}>
            <TextField
              label="Display name"
              autoComplete="name"
              placeholder="How you'll appear to other players"
              required
              error={Boolean(errors.display_name)}
              helperText={errors.display_name?.message ?? "Shown on your profile, in queues and on the ladder."}
              {...register("display_name")}
            />
            <TextField
              label="Date of birth"
              type="date"
              autoComplete="bday"
              required
              error={Boolean(errors.date_of_birth)}
              helperText={
                errors.date_of_birth?.message ??
                "Used for junior and adult events (U13–U19, Open, 35+). Under 13 needs a guardian's consent."
              }
              slotProps={{ inputLabel: { shrink: true } }}
              {...register("date_of_birth")}
            />
            <Stack spacing={1}>
              <SectionLabel id="gender-label">Gender</SectionLabel>
              <Controller
                control={control}
                name="gender"
                render={({ field }) => (
                  <RadioGroup
                    row
                    aria-labelledby="gender-label"
                    value={field.value ?? ""}
                    onChange={(event) => field.onChange(event.target.value)}
                    sx={{ gap: 1 }}
                  >
                    {GENDER_OPTIONS.map((option) => {
                      const checked = field.value === option.value;
                      return (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          label={option.label}
                          control={
                            <Radio
                              size="small"
                              slotProps={{ input: { "aria-checked": checked } }}
                              sx={HIDDEN_RADIO_SX}
                            />
                          }
                          sx={{
                            position: "relative",
                            m: 0,
                            px: 2,
                            py: 0.75,
                            borderRadius: 999,
                            border: 1,
                            borderColor: checked ? "primary.main" : "divider",
                            bgcolor: checked ? "primary.main" : "transparent",
                            color: checked ? "primary.contrastText" : "text.primary",
                            "& .MuiFormControlLabel-label": { fontWeight: 600, fontSize: "0.875rem" },
                            "&:has(input:focus-visible)": { outline: 2, outlineColor: "primary.main", outlineOffset: 2 },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                )}
              />
              <FormHelperText error={Boolean(errors.gender)} sx={{ mx: 0 }}>
                {errors.gender?.message ??
                  "Used to match you to men's, women's and mixed events (MS · WS · MD · WD · XD). “Prefer not to say” limits you to open events."}
              </FormHelperText>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
              <LockRounded fontSize="small" sx={{ mt: 0.25 }} />
              <Typography variant="body2">
                Date of birth and gender can&apos;t be changed once you create your profile. If something is wrong,
                contact support. Your date of birth is never shown on your profile.
              </Typography>
            </Stack>
          </Stack>
        ) : null}

        {step === 1 ? (
          <Stack spacing={2.5}>
            <Controller
              control={control}
              name="play_formats"
              render={({ field }) => <PlayFormatChips id="formats-label" value={field.value} onChange={field.onChange} />}
            />
            <Controller
              control={control}
              name="skill_level"
              render={({ field }) => <SkillLevelPicker id="level-label" value={field.value} onChange={field.onChange} />}
            />
            <Stack spacing={1}>
              <SectionLabel id="area-label">Home area</SectionLabel>
              <MaterialAreaPicker
                label="Area"
                value={areaDraft}
                onValueChange={(text) => {
                  setAreaDraft(text);
                  // Editing or clearing the text drops the earlier pick.
                  if (getValues("home_area")?.label !== text) setValue("home_area", undefined, { shouldDirty: true });
                }}
                onAreaSelect={(area) => {
                  setAreaDraft(area.label);
                  setValue("home_area", area, { shouldDirty: true });
                }}
                unavailableHelperText="You can add your area later in Edit profile."
              />
              <FormHelperText sx={{ mx: 0 }}>Used to suggest places and players near you. Optional.</FormHelperText>
            </Stack>
          </Stack>
        ) : null}

        {step === 2 ? (
          <Stack spacing={2.5}>
            <input type="hidden" {...register("avatar")} />
            <ProfilePhotoUploadField
              value={avatarValue}
              onChange={(url) => setValue("avatar", url, { shouldValidate: true, shouldDirty: true })}
              disabled={submitting}
              validationMessage={errors.avatar?.message}
            />
            <TextField
              label="About you (optional)"
              multiline
              minRows={3}
              placeholder="A sentence about your game"
              {...register("bio")}
            />
            {errorSlot}
            {consent}
          </Stack>
        ) : null}
      </Stack>

      <Box
        sx={{
          position: "fixed",
          insetInline: 0,
          bottom: 0,
          zIndex: 2,
          bgcolor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
          px: 2,
          py: 1.5,
          pb: "calc(12px + env(safe-area-inset-bottom, 0px))",
        }}
      >
        <Stack direction="row" spacing={2} sx={{ maxWidth: 560, mx: "auto", alignItems: "center" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700 }}>Step {step + 1} of {STEPS.length}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {current.note}
            </Typography>
          </Box>
          {lastStep ? (
            <Button
              key="create"
              type="submit"
              variant="contained"
              disabled={!isValid || submitting || !consentAccepted}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" aria-hidden="true" /> : undefined}
              sx={{ borderRadius: 999, px: 3 }}
            >
              Create profile
            </Button>
          ) : (
            <Button key="continue" type="button" variant="contained" onClick={() => void goNext()} sx={{ borderRadius: 999, px: 3 }}>
              Continue
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
